/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import NewModal from '../../../Shared/NewModal';
import { PatientTypeSelection } from './components/PatientTypeSelection';
import { ExistingPatientAssignment } from './components/ExistingPatientAssignment';
import { NewPatientForm } from './components/NewPatientForm';
import { StatusMessage } from './components/StatusMessage';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  clearDeviceActionStatus,
  fetchDeviceById,
  getDeviceList,
  linkDeviceToOwner,
} from '../../../../store/devices/actionCreators';
import { DeviceActionType } from '../../../../shared/constants';
import {
  AddHubOwnerParams,
  HubApiActions,
} from '../../../../store/hubOwner/types';
import {
  addHubOwner,
  clearHubActionStatus,
} from '../../../../store/hubOwner/actionCreators';
import { Device } from '../../../../store/devices/deviceTypes';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../images/icons/update_sw_success.svg';
import { ReactComponent as FailedToAddUserIcon } from '../../../../images/icons/adding_user_failed.svg';
import { Box, CircularProgress } from '@mui/material';
import {
  clearCustomerActionStatus,
  uploadProfilePicture,
} from '../../../../store/customers/actionCreators';

const AssignHubController = ({
  onClose,
  device_id,
  selectedDeviceDetails,
}: {
  onClose: () => void;
  device_id: number;
  selectedDeviceDetails: Device | null;
}) => {
  const dispatch = useDispatch();
  const { actionStatus } = useSelector((state: RootState) => state.devices);
  const { isAddingHubOwner, actionStatus: hubOwnerActionStatus } = useSelector(
    (state: RootState) => state.hubOwner,
  );
  const {
    actionStatus: customerActionStatus,
    isLoading: profileUploadLoading,
  } = useSelector((state: RootState) => state.customers);

  const STEPS = {
    SELECT_TYPE: 'select_type',
    EXISTING_PATIENT: 'existing_patient',
    NEW_PATIENT: 'new_patient',
    SUCCESS: 'success',
    ERROR: 'error',
    UPLOAD_PHOTO: 'upload_photo',
  };

  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_TYPE);
  const [addPhoto, setAddPhoto] = useState<File | null>(null);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  // Duplicate-check follow-up (409 action=get_phone): re-open the form with
  // the submitted values and a now-required phone field
  const [requirePhone, setRequirePhone] = useState(false);
  const [lastSubmittedValues, setLastSubmittedValues] = useState<any | null>(
    null,
  );

  // Handle patient type selection
  const handlePatientTypeSelect = (type: string) => {
    if (type === 'existing') {
      setCurrentStep(STEPS.EXISTING_PATIENT);
    } else {
      setCurrentStep(STEPS.NEW_PATIENT);
    }
  };

  // Handle assignment to an existing patient
  const handleExistingPatientAssignment = (customer_id: number) => {
    dispatch(linkDeviceToOwner(device_id, customer_id));
  };

  // Handle new Patient assignment
  const handleNewPatient = useCallback(
    (formValues: any) => {
      const {
        photo,
        facility_id,
        facility_location_id,
        isFromFacility,
        ...patientData
      } = formValues;

      const payload: AddHubOwnerParams = {
        ...patientData,
        device_id: device_id,
      };

      // Add facility_id to payload if device belongs to a facility
      if (selectedDeviceDetails?.facility_id) {
        payload.facility_id = selectedDeviceDetails.facility_id;
      }

      setAddPhoto(photo || null);
      setLastSubmittedValues(formValues);
      dispatch(addHubOwner(payload));
    },
    [device_id, dispatch, selectedDeviceDetails],
  );

  const handleClose = useCallback(() => {
    dispatch(clearCustomerActionStatus());
    dispatch(clearHubActionStatus());
    setIsPhotoUploading(false);
    setAddPhoto(null);
    setRequirePhone(false);
    setLastSubmittedValues(null);
    onClose();
  }, [dispatch, onClose]);

  // Handle Hub Owner status changes
  useEffect(() => {
    if (hubOwnerActionStatus.success) {
      if (!addPhoto) {
        // Case: Success without photo
        setCurrentStep(STEPS.SUCCESS);
        dispatch(fetchDeviceById(device_id));
        dispatch(getDeviceList());
      } else if (!isPhotoUploading && hubOwnerActionStatus.data) {
        // Case: Success with photo - trigger photo upload once
        setIsPhotoUploading(true);
        dispatch(uploadProfilePicture(hubOwnerActionStatus.data, addPhoto));
      }
    } else if (hubOwnerActionStatus.error) {
      if (hubOwnerActionStatus.errorAction === 'get_phone') {
        // Possible duplicate — send the user back to the form to add a
        // phone number for verification instead of showing an error screen
        dispatch(clearHubActionStatus());
        setRequirePhone(true);
        setCurrentStep(STEPS.NEW_PATIENT);
      } else {
        setCurrentStep(STEPS.ERROR);
      }
    }
  }, [hubOwnerActionStatus, addPhoto, device_id, dispatch, isPhotoUploading]);

  // Handle Profile Picture upload status
  useEffect(() => {
    if (customerActionStatus.success && isPhotoUploading) {
      setCurrentStep(STEPS.SUCCESS);
      dispatch(fetchDeviceById(device_id));
      dispatch(getDeviceList());
      setIsPhotoUploading(false);
      setAddPhoto(null);
    } else if (customerActionStatus.error && isPhotoUploading) {
      setCurrentStep(STEPS.ERROR);
      setIsPhotoUploading(false);
    }
  }, [customerActionStatus, device_id, dispatch, isPhotoUploading]);
  useEffect(() => {
    // Handle api status changes
    if (actionStatus.type === DeviceActionType.LinkDeviceToOwner) {
      if (actionStatus.success) {
        setCurrentStep(STEPS.SUCCESS);
        dispatch(clearDeviceActionStatus());
        dispatch(fetchDeviceById(device_id));
        dispatch(getDeviceList());
      } else {
        setCurrentStep(STEPS.ERROR);
        dispatch(clearDeviceActionStatus());
      }
    }
  }, [actionStatus]);

  const renderStepContent = () => {
    if (isAddingHubOwner || profileUploadLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      );
    }
    switch (currentStep) {
      case STEPS.SELECT_TYPE:
        return (
          <PatientTypeSelection
            onNext={handlePatientTypeSelect}
            onCancel={onClose}
          />
        );
      case STEPS.EXISTING_PATIENT:
        return (
          <ExistingPatientAssignment
            onAdd={handleExistingPatientAssignment}
            onCancel={() => setCurrentStep(STEPS.SELECT_TYPE)}
            selectedDeviceDetails={selectedDeviceDetails}
          />
        );
      case STEPS.NEW_PATIENT:
      case STEPS.UPLOAD_PHOTO:
        return (
          <NewPatientForm
            onAdd={handleNewPatient}
            onCancel={() => setCurrentStep(STEPS.SELECT_TYPE)}
            setCurrentStep={setCurrentStep}
            requirePhone={requirePhone}
            prefillValues={lastSubmittedValues}
          />
        );
      case STEPS.SUCCESS:
        return (
          <StatusMessage
            icon={<UpdateSwSuccessIcon />}
            title="Added successfully!"
            onClose={() => {
              onClose();
              dispatch(clearCustomerActionStatus());
              dispatch(clearHubActionStatus());
            }}
          />
        );
      case STEPS.ERROR:
        return (
          <StatusMessage
            icon={<FailedToAddUserIcon />}
            title="Patient didn’t save"
            message={
              hubOwnerActionStatus.errorAction === 'contact_cc'
                ? 'Something went wrong. Please contact customer care.'
                : 'Please try again or come back later.'
            }
            onClose={() => {
              if (
                hubOwnerActionStatus.error &&
                hubOwnerActionStatus.apiAction === HubApiActions.ADD_HUB_OWNER
              ) {
                dispatch(clearHubActionStatus());
                setCurrentStep(STEPS.NEW_PATIENT);
              } else {
                dispatch(clearCustomerActionStatus());
                dispatch(clearHubActionStatus());
                onClose();
              }
            }}
            primaryButtonText="Try again"
          />
        );
      default:
        return null;
    }
  };

  return (
    <NewModal
      showCloseX
      title="Add new"
      onClose={handleClose}
      maxWidth={[STEPS.NEW_PATIENT].includes(currentStep) ? 'sm' : 'xs'}
    >
      {renderStepContent()}
    </NewModal>
  );
};

export default AssignHubController;
